import { useState } from 'react'
import { Link, Head, usePage, router } from '@inertiajs/react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { VerifiedBadge } from '@/components/VerifiedBadge'
import EventCard from '@/components/EventCard'
import ShareButtons from '@/components/ShareButtons'
import type { DetailUser, EventListItem, UserListItem, SharedProps } from '@/types'

const PLACEHOLDER_COLORS = [
  'bg-primary/10',
  'bg-secondary/20',
  'bg-zinc-100 dark:bg-zinc-800',
  'bg-primary/5',
  'bg-secondary/10',
  'bg-zinc-50 dark:bg-zinc-800/50',
]

function hashIndex(id: string, len: number): number {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0
  }
  return Math.abs(hash) % len
}

type FollowedOrganization = {
  id: string
  name: string
  slug: string
  description: string | null
  primary_cause: string | null
  logo_url?: string | null
  verified?: boolean
}

type ProfileProps = {
  user: DetailUser
  followed_organizations: FollowedOrganization[]
  attended_events: EventListItem[]
  saved_events: EventListItem[]
  follower_count: number
  following_count: number
  is_following: boolean
  followers: UserListItem[]
  following: UserListItem[]
}

function displayName(user: DetailUser): string | null {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`
  }
  return null
}

function avatarInitials(user: DetailUser): string {
  if (user.first_name && user.last_name) {
    return `${user.first_name[0]}${user.last_name[0]}`
  }
  return '?'
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
    </svg>
  )
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

function UserList({ users, emptyMessage }: { users: UserListItem[]; emptyMessage: string }) {
  if (users.length === 0) {
    return <p className="py-4 text-center text-muted-foreground">{emptyMessage}</p>
  }
  return (
    <div className="mt-2 max-h-64 space-y-1 overflow-y-auto">
      {users.map(u => (
        <Link key={u.id} href={`/u/${u.username}`} className="flex items-center gap-3 rounded-xl p-2 transition-colors hover:bg-muted/50">
          <Avatar>
            {u.avatar_url && <AvatarImage src={u.avatar_url} alt={u.username} />}
            <AvatarFallback>{u.first_name?.[0] ?? '?'}{u.last_name?.[0] ?? ''}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            {u.first_name && u.last_name && (
              <p className="truncate text-base font-semibold">{u.first_name} {u.last_name}</p>
            )}
            <p className="truncate text-base text-muted-foreground">@{u.username}</p>
          </div>
        </Link>
      ))}
    </div>
  )
}

function FollowDialog({ follower_count, following_count, followers, following, defaultTab }: {
  follower_count: number
  following_count: number
  followers: UserListItem[]
  following: UserListItem[]
  defaultTab: string
}) {
  const [tab, setTab] = useState(defaultTab)

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Connections</DialogTitle>
      </DialogHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList variant="line">
          <TabsTrigger value="followers">Followers ({follower_count})</TabsTrigger>
          <TabsTrigger value="following">Following ({following_count})</TabsTrigger>
        </TabsList>
        <TabsContent value="followers">
          <UserList users={followers} emptyMessage="No followers yet" />
        </TabsContent>
        <TabsContent value="following">
          <UserList users={following} emptyMessage="Not following anyone yet" />
        </TabsContent>
      </Tabs>
    </DialogContent>
  )
}

function orgInitials(name: string): string {
  return name.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
}

export default function ProfileShow({
  user,
  followed_organizations,
  attended_events,
  saved_events,
  follower_count,
  following_count,
  is_following,
  followers,
  following,
}: ProfileProps) {
  const { current_user, app_url } = usePage<SharedProps>().props
  const [privateFollowConfirmOpen, setPrivateFollowConfirmOpen] = useState(false)
  const name = displayName(user)
  const ogTitle = `${name ?? user.username} — Gala Season`
  const ogDescription = user.bio ? user.bio.slice(0, 160) : `${name ?? user.username} — philanthropist profile on Gala Season.`
  const ogImage = user.avatar_url || `${app_url}/meta.png`
  const ogUrl = `${app_url}/u/${user.username}`
  const currentUserHasPrivateProfile = current_user?.visibility === false

  function followUser(confirmedPrivateProfileVisibility = false) {
    router.post(
      `/u/${user.username}/follow`,
      confirmedPrivateProfileVisibility ? { confirmed_private_profile_visibility: true } : {},
      {
        preserveScroll: true,
        onSuccess: () => setPrivateFollowConfirmOpen(false),
      }
    )
  }

  return (
    <>
      <Head title={name ?? user.username}>
        <meta head-key="description" name="description" content={ogDescription} />
        <meta head-key="og:title" property="og:title" content={ogTitle} />
        <meta head-key="og:description" property="og:description" content={ogDescription} />
        <meta head-key="og:type" property="og:type" content="profile" />
        <meta head-key="og:url" property="og:url" content={ogUrl} />
        <meta head-key="og:image" property="og:image" content={ogImage} />
        <meta head-key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta head-key="twitter:title" name="twitter:title" content={ogTitle} />
        <meta head-key="twitter:description" name="twitter:description" content={ogDescription} />
        <meta head-key="twitter:image" name="twitter:image" content={ogImage} />
      </Head>
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            href="/"
            className="text-base text-muted-foreground hover:text-foreground"
          >
            &larr; Back to events
          </Link>
        </div>

        {/* Hero banner */}
        {user.banner_url ? (
          <div className="relative h-64 overflow-hidden rounded-2xl sm:h-80">
            <img src={user.banner_url} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>
        ) : (
          <div className="relative flex h-48 items-center justify-center rounded-2xl bg-gradient-to-b from-[oklch(0.25_0.02_50)] via-[oklch(0.35_0.10_46)] to-primary sm:h-64">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt={name ?? user.username} className="h-24 w-24 rounded-full object-cover ring-4 ring-white/20" />
            ) : (
              <div className={`flex h-24 w-24 items-center justify-center rounded-full text-4xl font-semibold text-primary ${PLACEHOLDER_COLORS[hashIndex(user.id, PLACEHOLDER_COLORS.length)]}`}>
                {avatarInitials(user)}
              </div>
            )}
          </div>
        )}

        {/* Avatar overlay (when banner exists) */}
        {user.banner_url && (
          <div className="relative -mt-10 ml-6 inline-block">
            <div className="rounded-full bg-white p-1 shadow-md ring-1 ring-black/5">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt={name ?? user.username} className="h-20 w-20 rounded-full object-cover" />
              ) : (
                <div className={`flex h-20 w-20 items-center justify-center rounded-full text-3xl font-semibold text-primary ${PLACEHOLDER_COLORS[hashIndex(user.id, PLACEHOLDER_COLORS.length)]}`}>
                  {avatarInitials(user)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Profile info */}
        <div className="mb-10 mt-4">
          {name && (
            <h1 className="text-4xl font-semibold tracking-tight">{name}</h1>
          )}
          <div className="flex items-center gap-2">
            <p className="text-muted-foreground">@{user.username}</p>
            <Badge variant="secondary" className="text-sm">Philanthropist</Badge>
            {user.business && (
              <Badge variant="secondary" className="text-sm">Business</Badge>
            )}
          </div>

          {/* Follower/following counts */}
          <div className="mt-2 flex items-center gap-3 text-base text-muted-foreground">
            <Dialog>
              <DialogTrigger asChild>
                <button type="button" className="hover:text-foreground">
                  <span className="font-semibold text-foreground">{follower_count}</span>{' '}
                  {follower_count === 1 ? 'follower' : 'followers'}
                </button>
              </DialogTrigger>
              <FollowDialog
                follower_count={follower_count}
                following_count={following_count}
                followers={followers}
                following={following}
                defaultTab="followers"
              />
            </Dialog>
            <span className="text-muted-foreground/50">&middot;</span>
            <Dialog>
              <DialogTrigger asChild>
                <button type="button" className="hover:text-foreground">
                  <span className="font-semibold text-foreground">{following_count}</span> following
                </button>
              </DialogTrigger>
              <FollowDialog
                follower_count={follower_count}
                following_count={following_count}
                followers={followers}
                following={following}
                defaultTab="following"
              />
            </Dialog>
          </div>

          {/* Follow button */}
          {current_user && current_user.id !== user.id ? (
            <div className="mt-3">
              <Button
                variant={is_following ? 'default' : 'outline'}
                onClick={() => {
                  if (is_following) {
                    router.delete(`/u/${user.username}/follow`, { preserveScroll: true })
                  } else if (currentUserHasPrivateProfile) {
                    setPrivateFollowConfirmOpen(true)
                  } else {
                    followUser()
                  }
                }}
              >
                {is_following ? 'Following' : 'Follow'}
              </Button>
              <Dialog open={privateFollowConfirmOpen} onOpenChange={setPrivateFollowConfirmOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Follow with a private profile?</DialogTitle>
                    <DialogDescription>
                      Your profile is private, so @{user.username} will receive a notification that you followed them,
                      but they will not be able to view your profile. Do you want to proceed?
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setPrivateFollowConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={() => followUser(true)}>Proceed</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          ) : !current_user ? (
            <div className="mt-3">
              <Button variant="outline" asChild>
                <Link href="/users/sign_in">Follow</Link>
              </Button>
            </div>
          ) : null}

          {/* Social icons */}
          {(user.social_x || user.social_linkedin || user.social_instagram || user.social_facebook) && (
            <div className="mt-3 flex items-center gap-3">
              {user.social_x && (
                <a
                  href={user.social_x}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="X profile"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <XIcon className="size-4" />
                </a>
              )}
              {user.social_linkedin && (
                <a
                  href={user.social_linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn profile"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <LinkedInIcon className="size-4" />
                </a>
              )}
              {user.social_instagram && (
                <a
                  href={user.social_instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram profile"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <InstagramIcon className="size-4" />
                </a>
              )}
              {user.social_facebook && (
                <a
                  href={user.social_facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook profile"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <FacebookIcon className="size-4" />
                </a>
              )}
            </div>
          )}

          <div className="mt-3 w-fit">
            <ShareButtons
              title={`Check out ${name ?? user.username} on Gala Season${user.bio ? ` — ${user.bio.slice(0, 100)}` : ''}`}
              url={typeof window !== 'undefined' ? window.location.href : ''}
              label="Share Profile"
            />
          </div>
        </div>

        {/* Bio & details */}
        {(user.bio || user.city || user.state || user.email) && (
          <div className="mb-10 space-y-2">
            {user.bio && (
              <p className="max-w-3xl text-muted-foreground text-pretty">{user.bio}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-base text-muted-foreground">
              {(user.city || user.state) && (
                <span>{[user.city, user.state].filter(Boolean).join(', ')}</span>
              )}
              {user.email && (
                <a
                  href={`mailto:${user.email}`}
                  className="hover:text-foreground"
                >
                  {user.email}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Causes & Professional Networks */}
        {(user.interested_causes.length > 0 || user.interested_industries.length > 0) && (
          <div className="mb-10 grid grid-cols-1 gap-8 md:grid-cols-2">
            {user.interested_causes.length > 0 && (
              <div>
                <h2 className="mb-3 text-xl font-semibold">My Causes</h2>
                <div className="flex flex-wrap gap-2">
                  {user.interested_causes.map((cause) => (
                    <Badge key={cause} variant="secondary">
                      {cause}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {user.interested_industries.length > 0 && (
              <div>
                <h2 className="mb-3 text-xl font-semibold">My Professional Networks</h2>
                <div className="flex flex-wrap gap-2">
                  {user.interested_industries.map((industry) => (
                    <Badge key={industry} variant="secondary">
                      {industry}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Saved Events */}
        {saved_events.length > 0 && (
          <div className="mb-10">
            <h2 className="mb-1 text-3xl font-semibold">Saved Events</h2>
            <p className="mb-6 text-base text-muted-foreground">Events this user has bookmarked</p>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 stagger-grid">
              {saved_events.map((event) => (
                <Link key={event.id} href={`/events/${event.slug}`} className="animate-fade-up">
                  <EventCard event={event} showOrganization />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Following Organizations */}
        {followed_organizations.length > 0 && (
          <div className="mb-10">
            <h2 className="mb-1 text-3xl font-semibold">Following Organizations</h2>
            <p className="mb-4 text-base text-muted-foreground">{followed_organizations.length} {followed_organizations.length === 1 ? 'organization' : 'organizations'}</p>
            <div className="space-y-1">
              {followed_organizations.map((org) => (
                <Link key={org.id} href={`/o/${org.slug}`} className="flex items-center gap-3 rounded-xl p-3 transition-colors hover:bg-muted/50">
                  {org.logo_url ? (
                    <img src={org.logo_url} alt={org.name} className="h-10 w-10 shrink-0 rounded-lg object-cover ring-1 ring-black/5" />
                  ) : (
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-semibold text-primary ${PLACEHOLDER_COLORS[hashIndex(org.id, PLACEHOLDER_COLORS.length)]}`}>
                      {orgInitials(org.name)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="flex items-center gap-1 text-base font-semibold truncate">{org.name}{org.verified && <VerifiedBadge className="size-3" />}</p>
                    {(org.description || org.primary_cause) && (
                      <p className="text-base text-muted-foreground line-clamp-1">{org.description || org.primary_cause}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Attending Events */}
        {attended_events.length > 0 && (
          <div>
            <h2 className="mb-6 text-3xl font-semibold">Attending Events</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 stagger-grid">
              {attended_events.map((event) => (
                <Link key={event.id} href={`/events/${event.slug}`} className="animate-fade-up">
                  <EventCard event={event} showOrganization />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
